import React, { useEffect, useState, useMemo } from "react";
import AppLayout from "@/components/AppLayout";
import { http, formatDate } from "@/lib/api";
import { useI18n } from "@/i18n";
import {
  Plus,
  X,
  Trash,
  Check,
  CalendarBlank,
  SquaresFour,
  CaretLeft,
  CaretRight,
  Clock,
  Warning,
  PencilSimple,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Field, inputCls } from "@/pages/PipelinePage";

const priorityColor = {
  low: { text: "#5A6B76", bg: "#F1F5F9", border: "#CBD5E1" },
  medium: { text: "#C28E0E", bg: "#FEF9C3", border: "#FDE047" },
  high: { text: "#D63230", bg: "#FEE2E2", border: "#FECACA" },
};

const statusOrder = ["open", "in_progress", "done"];
const statusLabel = { open: "Terbuka", in_progress: "Dikerjakan", done: "Selesai" };

const DAY_NAMES = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
const MONTH_NAMES = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember"
];

function isSameDay(d1, d2) {
  if (!d1 || !d2) return false;
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

function toLocalDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default function TasksPage() {
  const { t } = useI18n();
  const [tasks, setTasks] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem("tasks_view") || "board");
  const [calMode, setCalMode] = useState("month"); // 'month' | 'week'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");

  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [prefilledDueDate, setPrefilledDueDate] = useState("");

  const setViewPersist = (v) => {
    setView(v);
    localStorage.setItem("tasks_view", v);
  };

  const load = async () => {
    try {
      const { data } = await http.get("/tasks");
      setTasks(data);
    } catch (err) {
      console.error("Load tasks error:", err);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleStatus = async (task) => {
    const idx = statusOrder.indexOf(task.status);
    const next = statusOrder[(idx + 1) % statusOrder.length];
    try {
      await http.patch(`/tasks/${task.id}`, { title: task.title, status: next });
      load();
    } catch {
      toast.error("Gagal mengubah status tugas");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus tugas ini?")) return;
    try {
      await http.delete(`/tasks/${id}`);
      toast.success("Tugas dihapus");
      load();
    } catch {
      toast.error("Gagal menghapus tugas");
    }
  };

  // Filtered tasks
  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      const matchSearch =
        !search ||
        t.title.toLowerCase().includes(search.toLowerCase()) ||
        (t.description && t.description.toLowerCase().includes(search.toLowerCase()));
      const matchPriority = priorityFilter === "all" || t.priority === priorityFilter;
      return matchSearch && matchPriority;
    });
  }, [tasks, search, priorityFilter]);

  const grouped = useMemo(() => {
    return {
      open: filteredTasks.filter((t) => t.status === "open"),
      in_progress: filteredTasks.filter((t) => t.status === "in_progress"),
      done: filteredTasks.filter((t) => t.status === "done"),
    };
  }, [filteredTasks]);

  // Tasks with and without due dates
  const { tasksWithDates, undatedTasks } = useMemo(() => {
    const withDates = [];
    const undated = [];
    filteredTasks.forEach((t) => {
      if (t.due_date) {
        const d = new Date(t.due_date);
        if (!isNaN(d.getTime())) {
          withDates.push({ ...t, parsedDate: d });
        } else {
          undated.push(t);
        }
      } else {
        undated.push(t);
      }
    });
    return { tasksWithDates: withDates, undatedTasks: undated };
  }, [filteredTasks]);

  // Calendar Navigation
  const prevPeriod = () => {
    setCurrentDate((prev) => {
      if (calMode === "month") {
        return new Date(prev.getFullYear(), prev.getMonth() - 1, 1);
      } else {
        const d = new Date(prev);
        d.setDate(d.getDate() - 7);
        return d;
      }
    });
  };

  const nextPeriod = () => {
    setCurrentDate((prev) => {
      if (calMode === "month") {
        return new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
      } else {
        const d = new Date(prev);
        d.setDate(d.getDate() + 7);
        return d;
      }
    });
  };

  const goToday = () => {
    setCurrentDate(new Date());
  };

  // Calendar Days Computation
  const calendarDays = useMemo(() => {
    const today = new Date();
    if (calMode === "month") {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();

      const firstDayOfMonth = new Date(year, month, 1);
      const startDayIndex = firstDayOfMonth.getDay(); // 0 (Sun) to 6 (Sat)
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      const days = [];
      // Preceding month days
      const daysInPrevMonth = new Date(year, month, 0).getDate();
      for (let i = startDayIndex - 1; i >= 0; i--) {
        const d = new Date(year, month - 1, daysInPrevMonth - i);
        days.push({ date: d, isCurrentMonth: false, isToday: isSameDay(d, today) });
      }

      // Current month days
      for (let i = 1; i <= daysInMonth; i++) {
        const d = new Date(year, month, i);
        days.push({ date: d, isCurrentMonth: true, isToday: isSameDay(d, today) });
      }

      // Next month padding to complete grid rows
      const remaining = 35 - days.length > 0 ? 35 - days.length : (42 - days.length > 0 ? 42 - days.length : 0);
      for (let i = 1; i <= remaining; i++) {
        const d = new Date(year, month + 1, i);
        days.push({ date: d, isCurrentMonth: false, isToday: isSameDay(d, today) });
      }

      return days;
    } else {
      // Week view: 7 days starting from Sunday
      const curr = new Date(currentDate);
      const dayOfWeek = curr.getDay();
      const startOfWeek = new Date(curr);
      startOfWeek.setDate(curr.getDate() - dayOfWeek);

      const days = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(startOfWeek);
        d.setDate(startOfWeek.getDate() + i);
        days.push({
          date: d,
          isCurrentMonth: d.getMonth() === currentDate.getMonth(),
          isToday: isSameDay(d, today),
        });
      }
      return days;
    }
  }, [currentDate, calMode]);

  // Open modal for task creation on specific date
  const handleDateClick = (date) => {
    setEditingTask(null);
    setPrefilledDueDate(toLocalDateString(date));
    setShowModal(true);
  };

  // Open modal for editing
  const handleEditTask = (task) => {
    setEditingTask(task);
    setPrefilledDueDate("");
    setShowModal(true);
  };

  const isOverdue = (task) => {
    if (!task.due_date || task.status === "done") return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(task.due_date);
    return due < today;
  };

  return (
    <AppLayout>
      {/* 1. Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4 mb-5 sm:mb-8">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#64748B] font-bold">
            Task Management
          </div>
          <h1
            className="font-display text-2xl sm:text-4xl lg:text-5xl tracking-tight mt-1 text-[#173042]"
            data-testid="tasks-title"
          >
            Tugas Saya
          </h1>
          <p className="text-[#64748B] mt-1 text-xs sm:text-sm">
            Pantau rencana kerja, tenggat waktu follow-up, dan to-do list sertifikasi.
          </p>
        </div>

        {/* Action Bar */}
        <div className="flex flex-wrap items-center gap-2">
          {/* View Toggle */}
          <div
            className="flex bg-white rounded-lg border border-[#CBD5E1] p-0.5 shadow-2xs"
            data-testid="tasks-view-toggle"
          >
            <button
              onClick={() => setViewPersist("board")}
              data-testid="view-board-btn"
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                view === "board"
                  ? "bg-[#173B4D] text-white shadow-xs"
                  : "text-[#64748B] hover:text-[#173042]"
              }`}
            >
              <SquaresFour size={14} weight="bold" />
              <span>Status</span>
            </button>
            <button
              onClick={() => setViewPersist("calendar")}
              data-testid="view-calendar-btn"
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                view === "calendar"
                  ? "bg-[#173B4D] text-white shadow-xs"
                  : "text-[#64748B] hover:text-[#173042]"
              }`}
            >
              <CalendarBlank size={14} weight="bold" />
              <span>Kalender</span>
            </button>
          </div>

          {/* New Task Button */}
          <button
            onClick={() => {
              setEditingTask(null);
              setPrefilledDueDate(toLocalDateString(new Date()));
              setShowModal(true);
            }}
            data-testid="new-task-btn"
            className="bg-[#173B4D] hover:bg-[#112B38] text-white px-3.5 py-1.5 rounded-lg flex items-center gap-1.5 text-xs font-semibold shadow-xs transition-colors"
          >
            <Plus size={15} weight="bold" />
            <span>{t("action.newTask")}</span>
          </button>
        </div>
      </div>

      {/* 2. Controls & Filter Strip */}
      <div className="bg-white p-3 rounded-xl border border-[#E2E8F0] shadow-2xs mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 flex-1">
          {/* Search */}
          <div className="relative min-w-[140px] flex-1 sm:flex-initial">
            <MagnifyingGlass
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              placeholder="Cari tugas..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:w-48 pl-8 pr-2.5 py-1 bg-white border border-[#CBD5E1] rounded-lg text-xs text-[#173042] placeholder:text-slate-400 focus:outline-none focus:border-[#173B4D]"
            />
          </div>

          {/* Priority Filter */}
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="px-2.5 py-1 bg-white border border-[#CBD5E1] rounded-lg text-xs text-[#475569] focus:outline-none focus:border-[#173B4D]"
          >
            <option value="all">Semua Prioritas</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        {/* Calendar Navigation Bar (Only visible when view === 'calendar') */}
        {view === "calendar" && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Mode Switcher */}
            <div className="flex bg-[#F1F5F9] rounded-lg p-0.5">
              <button
                onClick={() => setCalMode("month")}
                className={`px-2.5 py-0.5 rounded text-xs font-semibold transition-colors ${
                  calMode === "month" ? "bg-white text-[#173042] shadow-2xs" : "text-[#64748B]"
                }`}
              >
                Bulan
              </button>
              <button
                onClick={() => setCalMode("week")}
                className={`px-2.5 py-0.5 rounded text-xs font-semibold transition-colors ${
                  calMode === "week" ? "bg-white text-[#173042] shadow-2xs" : "text-[#64748B]"
                }`}
              >
                Minggu
              </button>
            </div>

            {/* Prev / Next / Today */}
            <div className="flex items-center gap-1">
              <button
                onClick={prevPeriod}
                className="p-1 rounded-lg border border-[#CBD5E1] bg-white hover:bg-slate-50 text-[#173042]"
                title="Periode Sebelumnya"
              >
                <CaretLeft size={14} weight="bold" />
              </button>
              <button
                onClick={goToday}
                className="px-2.5 py-1 rounded-lg border border-[#CBD5E1] bg-white hover:bg-slate-50 text-xs font-semibold text-[#173042]"
              >
                Hari Ini
              </button>
              <button
                onClick={nextPeriod}
                className="p-1 rounded-lg border border-[#CBD5E1] bg-white hover:bg-slate-50 text-[#173042]"
                title="Periode Berikutnya"
              >
                <CaretRight size={14} weight="bold" />
              </button>
            </div>

            {/* Period Label */}
            <div className="font-bold text-xs sm:text-sm text-[#173042] min-w-[130px] text-right">
              {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
            </div>
          </div>
        )}
      </div>

      {/* 3. Main View: Board vs Calendar */}
      {view === "board" ? (
        /* ================= BOARD VIEW ================= */
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 sm:gap-4" data-testid="tasks-board">
          {statusOrder.map((s) => (
            <div
              key={s}
              className="bg-white rounded-xl border border-[#E2E8F0] p-4 sm:p-5 shadow-2xs flex flex-col"
            >
              <div className="flex items-center justify-between pb-3 border-b border-[#E2E8F0] mb-3 sm:mb-4">
                <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#173042]">
                  {statusLabel[s]}
                </div>
                <span className="font-mono-data text-xs font-bold text-[#64748B] bg-[#F1F5F9] px-2 py-0.5 rounded-full">
                  {grouped[s].length}
                </span>
              </div>

              <div className="space-y-2.5 sm:space-y-3 flex-1">
                {grouped[s].length === 0 && (
                  <div className="text-xs text-[#64748B] py-8 text-center italic">
                    Tidak ada tugas
                  </div>
                )}
                {grouped[s].map((task) => {
                  const overdue = isOverdue(task);
                  const pInfo = priorityColor[task.priority] || priorityColor.medium;

                  return (
                    <div
                      key={task.id}
                      data-testid={`task-${task.id}`}
                      className="rounded-lg border border-[#E2E8F0] p-3 hover:border-[#173B4D] hover:shadow-2xs transition-all bg-[#F8FAFC]/50 group"
                    >
                      <div className="flex items-start gap-2.5">
                        <button
                          onClick={() => toggleStatus(task)}
                          className={`w-5 h-5 rounded-md border shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                            task.status === "done"
                              ? "bg-[#2E8B57] border-[#2E8B57]"
                              : "border-[#CBD5E1] bg-white hover:border-[#173B4D]"
                          }`}
                        >
                          {task.status === "done" && (
                            <Check size={12} weight="bold" color="white" />
                          )}
                        </button>
                        <div
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => handleEditTask(task)}
                        >
                          <div
                            className={`text-xs sm:text-sm font-medium leading-snug break-words ${
                              task.status === "done"
                                ? "line-through text-[#64748B]"
                                : "text-[#173042]"
                            }`}
                          >
                            {task.title}
                          </div>
                          {task.description && (
                            <div className="text-[11px] text-[#64748B] mt-1 line-clamp-2">
                              {task.description}
                            </div>
                          )}
                          <div className="flex flex-wrap items-center gap-1.5 mt-2 text-[10px]">
                            <span
                              className="font-bold px-1.5 py-0.2 rounded"
                              style={{
                                color: pInfo.text,
                                backgroundColor: pInfo.bg,
                              }}
                            >
                              {task.priority}
                            </span>
                            {task.due_date && (
                              <span
                                className={`font-mono-data px-1.5 py-0.2 rounded flex items-center gap-1 ${
                                  overdue
                                    ? "bg-red-50 text-red-700 font-bold"
                                    : "text-[#64748B]"
                                }`}
                              >
                                {overdue && <Warning size={11} />}
                                {formatDate(task.due_date)}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-0.5 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleEditTask(task)}
                            className="p-1 rounded text-[#64748B] hover:text-[#173B4D] hover:bg-slate-100"
                            title="Edit Tugas"
                          >
                            <PencilSimple size={13} />
                          </button>
                          <button
                            onClick={() => del(task.id)}
                            className="p-1 rounded text-[#64748B] hover:text-red-600 hover:bg-red-50"
                            title="Hapus Tugas"
                          >
                            <Trash size={13} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ================= CALENDAR VIEW ================= */
        <div className="space-y-5" data-testid="tasks-calendar-view">
          {/* Calendar Grid Container */}
          <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-2xs overflow-hidden">
            {/* Day Header Row */}
            <div className="grid grid-cols-7 border-b border-[#E2E8F0] bg-[#F8FAFC] text-center font-bold text-[10px] sm:text-xs text-[#64748B] py-2">
              {DAY_NAMES.map((name, idx) => (
                <div key={idx} className={idx === 0 || idx === 6 ? "text-amber-800" : ""}>
                  {name}
                </div>
              ))}
            </div>

            {/* Calendar Cells */}
            <div className="grid grid-cols-7 divide-x divide-y divide-[#E2E8F0]">
              {calendarDays.map((cell, idx) => {
                const dayTasks = tasksWithDates.filter((t) =>
                  isSameDay(t.parsedDate, cell.date)
                );

                return (
                  <div
                    key={idx}
                    onClick={() => handleDateClick(cell.date)}
                    data-testid={`calendar-cell-${toLocalDateString(cell.date)}`}
                    className={`min-h-[85px] sm:min-h-[110px] p-1.5 sm:p-2 flex flex-col justify-between transition-colors hover:bg-slate-50 cursor-pointer ${
                      !cell.isCurrentMonth ? "bg-slate-50/50 text-slate-400" : "bg-white"
                    } ${cell.isToday ? "ring-2 ring-inset ring-[#0F7180]/40" : ""}`}
                  >
                    {/* Top Row: Date Number & Add Icon */}
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-xs font-mono font-bold w-5 h-5 rounded-full flex items-center justify-center ${
                          cell.isToday
                            ? "bg-[#173B4D] text-white"
                            : cell.isCurrentMonth
                            ? "text-[#173042]"
                            : "text-slate-400"
                        }`}
                      >
                        {cell.date.getDate()}
                      </span>
                      {dayTasks.length > 0 && (
                        <span className="text-[10px] font-mono text-[#64748B] font-semibold">
                          {dayTasks.length}
                        </span>
                      )}
                    </div>

                    {/* Task Pills in Cell */}
                    <div className="space-y-1 mt-1 flex-1 overflow-hidden">
                      {dayTasks.slice(0, 3).map((task) => {
                        const overdue = isOverdue(task);
                        const isDone = task.status === "done";

                        return (
                          <div
                            key={task.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditTask(task);
                            }}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium truncate flex items-center gap-1 transition-opacity hover:opacity-80 ${
                              isDone
                                ? "bg-emerald-50 text-emerald-800 line-through"
                                : overdue
                                ? "bg-red-50 text-red-800 border border-red-200 font-bold"
                                : "bg-[#173B4D]/10 text-[#173042]"
                            }`}
                            title={`${task.title} (${task.priority})`}
                          >
                            <span
                              className="w-1.5 h-1.5 rounded-full shrink-0"
                              style={{
                                backgroundColor: isDone
                                  ? "#2E8B57"
                                  : overdue
                                  ? "#D63230"
                                  : priorityColor[task.priority]?.text || "#C28E0E",
                              }}
                            />
                            <span className="truncate">{task.title}</span>
                          </div>
                        );
                      })}
                      {dayTasks.length > 3 && (
                        <div className="text-[9px] text-[#64748B] font-bold pl-1">
                          +{dayTasks.length - 3} lainnya
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Undated Tasks Section */}
          {undatedTasks.length > 0 && (
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 shadow-2xs">
              <div className="flex items-center justify-between pb-2 mb-3 border-b border-[#E2E8F0]">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-[#C28E0E]" />
                  <h3 className="font-bold text-xs uppercase tracking-wider text-[#173042]">
                    Tugas Tanpa Tenggat ({undatedTasks.length})
                  </h3>
                </div>
                <span className="text-[11px] text-[#64748B]">
                  Klik tugas untuk mengatur tanggal jatuh tempo
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {undatedTasks.map((task) => (
                  <div
                    key={task.id}
                    onClick={() => handleEditTask(task)}
                    className="p-2.5 rounded-lg border border-[#E2E8F0] hover:border-[#173B4D] hover:bg-slate-50 transition-all flex items-center justify-between gap-2 cursor-pointer"
                  >
                    <div className="min-w-0 flex-1">
                      <div
                        className={`text-xs font-semibold truncate ${
                          task.status === "done" ? "line-through text-[#64748B]" : "text-[#173042]"
                        }`}
                      >
                        {task.title}
                      </div>
                      <div className="text-[10px] text-[#64748B] mt-0.5 flex items-center gap-1.5">
                        <span className="capitalize">{statusLabel[task.status]}</span>
                        <span>·</span>
                        <span className="uppercase font-bold">{task.priority}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleStatus(task);
                      }}
                      className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                        task.status === "done"
                          ? "bg-[#2E8B57] border-[#2E8B57] text-white"
                          : "border-[#CBD5E1]"
                      }`}
                    >
                      {task.status === "done" && <Check size={11} weight="bold" />}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Task Modal (Create & Edit) */}
      {showModal && (
        <TaskModal
          initial={editingTask}
          prefilledDueDate={prefilledDueDate}
          onClose={() => {
            setShowModal(false);
            setEditingTask(null);
          }}
          onSaved={() => {
            setShowModal(false);
            setEditingTask(null);
            load();
          }}
        />
      )}
    </AppLayout>
  );
}

function TaskModal({ onClose, onSaved, initial, prefilledDueDate }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    title: "",
    description: "",
    due_date: prefilledDueDate || "",
    priority: "medium",
    status: "open",
    ...(initial
      ? {
          ...initial,
          due_date: initial.due_date ? initial.due_date.slice(0, 10) : "",
        }
      : {}),
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description || undefined,
        priority: form.priority,
        status: form.status,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
      };

      if (initial) {
        await http.patch(`/tasks/${initial.id}`, payload);
        toast.success("Tugas diperbarui");
      } else {
        await http.post("/tasks", payload);
        toast.success("Tugas dibuat");
      }
      onSaved();
    } catch {
      toast.error("Gagal menyimpan tugas");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-3 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl border border-[#E2E8F0] w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden page-enter"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 sm:px-6 py-3.5 sm:py-4 border-b border-[#E2E8F0] flex items-center justify-between bg-[#F8FAFC] shrink-0">
          <div>
            <h3 className="font-bold text-sm sm:text-base text-[#173042]">
              {initial ? "Edit Tugas" : "Tugas Baru"}
            </h3>
            <p className="text-[11px] sm:text-xs text-[#64748B] mt-0.5">
              Catat target tindak lanjut, due date, dan to-do sertifikasi
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded text-[#64748B] hover:text-[#173042] hover:bg-[#E2E8F0]"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <form onSubmit={submit} className="flex flex-col flex-1 overflow-hidden" data-testid="task-form">
          <div className="p-4 sm:p-6 space-y-3.5 sm:space-y-4 overflow-y-auto flex-1">
            <Field label={t("common.title")} required>
              <input
                required
                data-testid="task-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className={inputCls}
                placeholder="Judul tugas..."
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label={t("common.priority")}>
                <select
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className={inputCls}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </Field>

              <Field label="Status">
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className={inputCls}
                >
                  <option value="open">Terbuka</option>
                  <option value="in_progress">Dikerjakan</option>
                  <option value="done">Selesai</option>
                </select>
              </Field>

              <Field label={t("common.dueDate")}>
                <input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className={inputCls}
                />
              </Field>
            </div>

            <Field label="Deskripsi">
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className={inputCls + " min-h-[70px]"}
                placeholder="Rincian to-do..."
              />
            </Field>
          </div>

          <div className="px-4 sm:px-6 py-3 border-t border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-end gap-2.5 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-[#CBD5E1] text-xs font-semibold uppercase tracking-wider text-[#64748B] hover:text-[#173042] hover:bg-[#F8FAFC]"
            >
              {t("action.cancel")}
            </button>
            <button
              disabled={saving}
              type="submit"
              data-testid="task-submit"
              className="bg-[#173B4D] hover:bg-[#112B38] text-white px-5 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider disabled:opacity-60 transition-colors shadow-xs"
            >
              {saving ? "..." : initial ? "Simpan Perubahan" : t("action.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
